import { Badge } from "@/components/ui/badge";
import { AgentName } from "@/components/molecules/agent-name";
import {
  connectorTypeLabel,
  type Connector,
} from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";
import { BetaBadge } from "./beta-badge";
import { ChannelLogo } from "./channel-logo";
import { channelOf } from "./channels";
import { RegistrationBadge } from "./registration-badge";

export const useConnectorColumns = (): ColumnDef<Connector>[] => {
  const { t } = useTranslation("connectors");

  return [
    {
      accessorKey: "type",
      header: t("table.type"),
      cell: ({ row }) => {
        const type = row.original.type;
        const channel = row.original.configuration?.meta?.channel;
        return (
          <div className="flex items-center gap-2">
            <ChannelLogo kind={channelOf(row.original)} size="sm" />
            <span>
              {type === "meta" && channel
                ? t(`meta.${channel}.label`)
                : connectorTypeLabel(type)}
            </span>
            <BetaBadge kind={channelOf(row.original)} />
          </div>
        );
      },
    },
    {
      accessorKey: "agent_id",
      header: t("table.agent"),
      cell: ({ row }) => {
        const agentId = row.original.agent_id;
        return (
          <div className="text-sm">
            {agentId ? (
              <AgentName
                agentId={agentId}
                fallback={t("table.unknownAgent")}
                avatarSize="sm"
              />
            ) : (
              <span className="text-muted-foreground">
                {t("table.defaultAgent")}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "capabilities",
      header: t("table.capabilities"),
      cell: ({ row }) => {
        const capabilities = row.original.capabilities || [];

        if (capabilities.length === 0) {
          return (
            <span className="text-muted-foreground text-xs">
              {t("table.noCapabilities")}
            </span>
          );
        }

        return (
          <div className="flex flex-wrap gap-1">
            {capabilities.map((capability) => (
              <Badge
                key={capability}
                variant="secondary"
                className="text-xs capitalize"
              >
                {t(`form.${capability}Label`, capability)}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "configuration",
      header: t("table.phoneNumbers"),
      cell: ({ row }) => {
        const config = row.original.configuration;
        const phoneNumbers = config?.phone_numbers || [];
        const whatsappNumbers = config?.whatsapp_numbers || [];

        // A Meta account has no number of ours: what identifies it is its name.
        if (row.original.type === "meta") {
          const meta = config?.meta;
          return meta?.display_name ? (
            <span className="text-sm">{meta.display_name}</span>
          ) : (
            <span className="text-muted-foreground text-xs">
              {t("table.noNumbers")}
            </span>
          );
        }

        // A "temporary" connector owns no number: it declares the numbers its
        // owner calls the shared demo number from.
        if (row.original.type === "temporary") {
          const callerNumbers = config?.caller_numbers || [];
          if (callerNumbers.length === 0) {
            return (
              <span className="text-muted-foreground text-xs">
                {t("table.noNumbers")}
              </span>
            );
          }
          return (
            <div className="flex flex-col gap-1">
              {callerNumbers.slice(0, 3).map((number) => (
                <Badge
                  key={number}
                  variant="outline"
                  className="font-mono text-xs w-fit"
                >
                  {number}
                </Badge>
              ))}
              {callerNumbers.length > 3 && (
                <span className="text-muted-foreground text-xs">
                  {t("table.more", { count: callerNumbers.length - 3 })}
                </span>
              )}
            </div>
          );
        }

        // A mailbox owns an address, not a number.
        if (row.original.type === "email") {
          const address = config?.email_account?.address;
          return address ? (
            <Badge variant="outline" className="font-mono text-xs w-fit">
              {address}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs">
              {t("table.noNumbers")}
            </span>
          );
        }

        if (phoneNumbers.length === 0 && whatsappNumbers.length === 0) {
          return (
            <span className="text-muted-foreground text-xs">
              {t("table.noNumbers")}
            </span>
          );
        }

        return (
          <div className="flex flex-col gap-1">
            {phoneNumbers.slice(0, 2).map((number) => (
              <Badge
                key={number}
                variant="outline"
                className="font-mono text-xs w-fit"
              >
                {number}
              </Badge>
            ))}
            {whatsappNumbers.slice(0, 1).map((number) => (
              <Badge
                key={number}
                variant="secondary"
                className="font-mono text-xs w-fit"
              >
                WhatsApp: {number.replace("whatsapp:", "")}
              </Badge>
            ))}
            {phoneNumbers.length + whatsappNumbers.length > 3 && (
              <span className="text-muted-foreground text-xs">
                {t("table.more", {
                  count: phoneNumbers.length + whatsappNumbers.length - 3,
                })}
              </span>
            )}
          </div>
        );
      },
    },
    // No "Account" column. It showed `configuration.account_id` — the Twilio
    // account SID, truncated — which is a credential identifier, not something a
    // client recognises or can act on: for a SIP or web connector it was always
    // "-", and for a temporary one it is a *borrowed* account belonging to another
    // tenant, which is worse than useless. The row already says which channel it
    // is and which numbers are on it.
    {
      accessorKey: "status",
      header: t("table.status"),
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "active"
            ? "default"
            : status === "inactive"
              ? "secondary"
              : "destructive";

        return (
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={variant}>{t(`status.${status}`, status)}</Badge>
            {row.original.configuration?.demo_number && (
              <Badge variant="outline" className="text-xs">
                {t("table.demo")}
              </Badge>
            )}
            {row.original.configuration?.demo_number &&
              row.original.configuration?.code_routing && (
                <Badge variant="outline" className="text-xs">
                  {t("table.codeRouting")}
                </Badge>
              )}
            {row.original.configuration?.default_connector && (
              <Badge variant="outline" className="text-xs">
                {t("table.default")}
              </Badge>
            )}
            {row.original.configuration?.meta?.needs_reconnect && (
              <Badge variant="destructive" className="text-xs">
                {t("meta.needsReconnect")}
              </Badge>
            )}
            <RegistrationBadge registration={row.original.registration} />
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action connector={row.original} />,
    },
  ];
};
